// Leatherwood (lwr-) store reviews: lightbox (native <dialog>), "Read more", scroller arrows.
// Loaded as a module by sections/lwr-reviews.liquid and sections/lwr-review-photos.liquid;
// the browser runs it once per page. Everything is event-delegated, so editor re-renders work.
// [data-lwr-scope] = one Review cards / Photo grid block (a duplicated block gets its own arrows).
const SEL_TEXT = '[data-lwr-text]';
let dialog;
let list = [];
let at = 0;

const icon = (d) => `<svg viewBox="0 0 24 24" aria-hidden="true"><path d="${d}" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>`;

function getDialog() {
  if (dialog) return dialog;
  dialog = document.createElement('dialog');
  dialog.className = 'lwr-dialog';
  dialog.setAttribute('aria-label', 'Customer review');
  dialog.innerHTML =
    `<button type="button" class="lwr-dialog__close" aria-label="Close" data-lwr-close>${icon('M6 6l12 12M18 6L6 18')}</button>` +
    `<button type="button" class="lwr-dialog__nav lwr-dialog__nav--prev" aria-label="Previous review" data-lwr-go="-1">${icon('M15 5l-7 7 7 7')}</button>` +
    `<button type="button" class="lwr-dialog__nav lwr-dialog__nav--next" aria-label="Next review" data-lwr-go="1">${icon('M9 5l7 7-7 7')}</button>` +
    '<div class="lwr-dialog__content"></div><p class="lwr-dialog__count" aria-live="polite"></p>';
  dialog.addEventListener('click', (e) => {
    // Backdrop (outside the content box) or the close button closes; arrows move between reviews / photos.
    if (e.target === dialog || e.target.closest('[data-lwr-close]')) return dialog.close();
    const go = e.target.closest('[data-lwr-go]');
    if (go) return show(at + Number(go.dataset.lwrGo));
    const ph = e.target.closest('[data-lwr-ph]');
    if (ph) return photoStep(Number(ph.dataset.lwrPh));
    const dot = e.target.closest('[data-lwr-dot]');
    if (dot) return photoTo(Number(dot.dataset.lwrDot));
  });
  dialog.addEventListener('keydown', (e) => {
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      e.preventDefault();
      show(at + (e.key === 'ArrowLeft' ? -1 : 1));
    }
  });
  dialog.addEventListener('close', () => {
    dialog.querySelector('.lwr-dialog__content').replaceChildren();
    document.documentElement.classList.remove('lwr-dialog-open');
  });
  document.body.appendChild(dialog);
  return dialog;
}

// Photos of the open review: dots + small arrows replace the bare scrollbar; swiping still works (scroll snap).
const track = () => dialog?.querySelector('[data-lwr-full-photos]');
function photoTo(i) {
  const t = track();
  if (!t || !t.children[i]) return;
  t.scrollTo({ left: t.children[i].offsetLeft, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
}
function photoAt() {
  const t = track();
  return t ? Math.round(t.scrollLeft / Math.max(1, t.clientWidth)) : 0;
}
function photoStep(d) {
  const t = track();
  if (!t) return;
  const n = t.children.length;
  photoTo((photoAt() + d + n) % n);
}
function photoUi() {
  const t = track();
  if (!t || t.children.length < 2) return;
  const wrap = t.parentElement;
  const n = t.children.length;
  wrap.insertAdjacentHTML(
    'beforeend',
    `<button type="button" class="lwr-full__ph lwr-full__ph--prev" aria-label="Previous photo" data-lwr-ph="-1">${icon('M15 5l-7 7 7 7')}</button>` +
      `<button type="button" class="lwr-full__ph lwr-full__ph--next" aria-label="Next photo" data-lwr-ph="1">${icon('M9 5l7 7-7 7')}</button>` +
      `<div class="lwr-full__dots">${Array.from({ length: n }, (_, i) => `<button type="button" class="lwr-full__dot" aria-label="Photo ${i + 1} of ${n}" data-lwr-dot="${i}"></button>`).join('')}</div>`
  );
  const mark = () => {
    const i = photoAt();
    wrap.querySelectorAll('.lwr-full__dot').forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
  };
  t.addEventListener('scroll', () => requestAnimationFrame(mark), { passive: true });
  mark();
}

function render(holder, photoIndex) {
  const tpl = holder.querySelector('template[data-lwr-full]');
  if (!tpl) return false;
  const d = getDialog();
  const content = d.querySelector('.lwr-dialog__content');
  content.replaceChildren(tpl.content.cloneNode(true));
  const t = content.querySelector('[data-lwr-full-photos]');
  if (t) {
    const wrap = document.createElement('div');
    wrap.className = 'lwr-full__media';
    t.replaceWith(wrap);
    wrap.appendChild(t);
    photoUi();
    if (photoIndex > 0 && t.children[photoIndex]) t.scrollLeft = t.children[photoIndex].offsetLeft;
  }
  // The dialog lives on <body>, outside the section: carry the section's star colour over.
  const scope = holder.closest('[data-lwr-scope]');
  const star = scope ? getComputedStyle(scope).getPropertyValue('--lwr-star-color').trim() : '';
  if (star) d.style.setProperty('--lwr-star-color', star);
  return true;
}

// Every review of the same grid / card row, so the lightbox can step through them without closing.
function show(i, photoIndex = 0) {
  if (!list.length) return;
  at = (i + list.length) % list.length;
  if (!render(list[at], photoIndex)) return;
  const d = getDialog();
  const many = list.length > 1;
  d.classList.toggle('lwr-dialog--many', many);
  d.querySelector('.lwr-dialog__count').textContent = many ? `${at + 1} / ${list.length}` : '';
  // Warm the next review's first photo.
  const nx = list[(at + 1) % list.length]?.querySelector('template[data-lwr-full]')?.content.querySelector('img');
  if (nx && many) { const im = new Image(); im.srcset = nx.srcset; im.sizes = nx.sizes; im.src = nx.src; }
}

function openReview(holder, photoIndex) {
  const scope = holder.closest('[data-lwr-scope]') || document;
  list = [...scope.querySelectorAll('[data-lwr-review]')].filter((h) => h.querySelector('template[data-lwr-full]'));
  const d = getDialog();
  show(Math.max(0, list.indexOf(holder)), photoIndex);
  document.documentElement.classList.add('lwr-dialog-open');
  if (!d.open) d.showModal();
}

function updateArrows(scope) {
  const track = scope.querySelector('[data-lwr-track]');
  if (!track) return;
  const prev = scope.querySelector('[data-lwr-prev]');
  const next = scope.querySelector('[data-lwr-next]');
  const max = track.scrollWidth - track.clientWidth - 2;
  if (prev) prev.disabled = track.scrollLeft <= 2;
  if (next) next.disabled = track.scrollLeft >= max;
}

function initScope(scope) {
  // Hide "Read more" where the text is not actually clamped.
  scope.querySelectorAll(SEL_TEXT).forEach((text) => {
    const more = text.parentElement.querySelector('[data-lwr-more]');
    if (more && !text.classList.contains('is-open')) more.hidden = text.scrollHeight <= text.clientHeight + 2;
  });
  const track = scope.querySelector('[data-lwr-track]');
  if (track && !track.dataset.lwrBound) {
    track.dataset.lwrBound = '1';
    track.addEventListener('scroll', () => updateArrows(scope), { passive: true });
  }
  updateArrows(scope);
}

function initAll() {
  document.querySelectorAll('[data-lwr-scope]').forEach(initScope);
}

document.addEventListener('click', (e) => {
  const open = e.target.closest('[data-lwr-open]');
  if (open) {
    const holder = open.closest('[data-lwr-review]');
    if (holder) {
      e.preventDefault();
      openReview(holder, parseInt(open.dataset.lwrPhoto || '0', 10));
    }
    return;
  }
  const more = e.target.closest('[data-lwr-more]');
  if (more) {
    const text = more.parentElement.querySelector(SEL_TEXT);
    const isOpen = text.classList.toggle('is-open');
    more.setAttribute('aria-expanded', String(isOpen));
    more.textContent = isOpen ? 'Show less' : 'Read more';
    return;
  }
  const arrow = e.target.closest('[data-lwr-prev], [data-lwr-next]');
  if (arrow) {
    const scope = arrow.closest('[data-lwr-scope]');
    const track = scope && scope.querySelector('[data-lwr-track]');
    if (!track) return;
    const card = track.firstElementChild;
    const step = card ? card.getBoundingClientRect().width + 16 : track.clientWidth;
    const pageStep = Math.max(step, Math.floor(track.clientWidth / step) * step);
    track.scrollBy({ left: arrow.hasAttribute('data-lwr-prev') ? -pageStep : pageStep, behavior: 'smooth' });
  }
});

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
else initAll();
window.addEventListener('load', initAll);
document.addEventListener('shopify:section:load', initAll);
document.addEventListener('shopify:block:select', initAll);
let resizeTimer;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimer);
  resizeTimer = setTimeout(initAll, 150);
});
